/**
 * PLAIN ROWS (his words, 18 Sep 2026, rows 355, 357, 358 and 359): every Exceptions row said in plain
 * words from its FIELDS, never from the stored text that loaded it. Pure, so the screen and the tests
 * read the same sentences. The stored text (evidence, source files, import notes, change history)
 * stays on the record and is shown only behind "Technical detail", to the owner.
 *
 * Unknown values are NEVER blank and never guessed: a Post Office code with no words here reads
 * "The Post Office gave a code we do not have words for yet."
 */

export interface PostalLineView { line1: string; line2: string; city: string; state: string; zip: string; zip4?: string }

export interface PostalFactsView {
  campaign: string;
  plan: PostalLineView | null;
  postOffice: PostalLineView | null;
  suggested: string | null;
  result: string | null;
  flags: string[];
  outcome: string | null;
  dpv: string | null;
  vacant: string | null;
}

export const UNKNOWN_POST_OFFICE_CODE = 'The Post Office gave a code we do not have words for yet.';

/** The 26 FOBT pre-launch check's DPV letter, one sentence each. */
export const DPV_WORDS: Record<string, string> = {
  Y: 'The Post Office confirms this address.',
  N: 'The Post Office cannot deliver to this address.',
  S: 'The street address is good, but the apartment or suite number could not be confirmed.',
  D: 'The apartment or suite number is missing.',
};

/** The fuller check's findings (usps_result) and marks (usps_delivery_flags), one sentence each. */
export const FINDING_WORDS: Record<string, string> = {
  'deliverable as written': 'The Post Office can deliver to this address as written.',
  'deliverable after usps correction': 'The Post Office can deliver to this address once it is corrected.',
  'no delivery (no-stat)': 'The Post Office does not deliver to this address: it is not an active delivery point.',
  'no usps match': 'The Post Office found no such address.',
  undeliverable: 'The Post Office cannot deliver to this address.',
  'apartment or suite not confirmed': 'The apartment or suite number could not be confirmed.',
  'apartment or suite missing': 'The apartment or suite number is missing.',
  vacant: 'The address is vacant.',
  'no door delivery': 'The Post Office does not deliver to the door at this address.',
  'mailbox store': 'This address is a mailbox store.',
  business: 'This is a business address.',
  'shared drop point': 'Mail for this address goes to a shared drop point.',
  'matched the building only, no valid unit': 'Only the building matched; the unit number is not valid.',
  'street mail held for a po box': 'Street mail for this address is held for a PO Box.',
  // Our own verdicts (mail_outcome), in case a row carries only those.
  'not deliverable as supplied': 'The address cannot be delivered as the plan gave it.',
  'will not arrive': 'Mail to this address will not arrive.',
  'deliverable after correction': 'The Post Office can deliver to this address once it is corrected.',
  'deliverable review': 'The address may be deliverable; it needs a check.',
  // Mail that came back (the kit and letter scans).
  'kit returned to sender': 'The kit came back: the Post Office returned it to us.',
  'returned to sender': 'The Post Office returned the mail to us.',
};

export function findingSentence(word: string): string {
  const k = word.trim().replace(/^usps:\s*/i, '').toLowerCase();
  return FINDING_WORDS[k] ?? UNKNOWN_POST_OFFICE_CODE;
}

/**
 * A row's own reason as a sentence: the table's words when it has them; the reason itself, tidied,
 * when it is already plain ("USPS: street number with no street name"); the unknown-code sentence
 * only when it is in code form. So a plain reason is never replaced by "a code we have no words for".
 */
export function reasonSentence(reason: string): string {
  const known = FINDING_WORDS[reason.trim().replace(/^usps:\s*/i, '').toLowerCase()];
  if (known) return known;
  const plain = reason.trim().replace(/^usps:\s*/i, '').trim();
  if (!plain || CODE_LIKE.test(plain)) return UNKNOWN_POST_OFFICE_CODE;
  return plain[0]!.toUpperCase() + plain.slice(1) + (/[.!?]$/.test(plain) ? '' : '.');
}

/** "2025-03-03" as "Mar 3, 2025", read as a calendar day (never shifted a day by a time zone). */
export function fmtDay(ymd: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(ymd.trim());
  if (!m) return ymd;
  const month = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][Number(m[2]) - 1];
  return month ? `${month} ${Number(m[3])}, ${m[1]}` : ymd;
}

/** "123 MAIN ST APT 4, MODESTO CA 95350-1234". */
export function fmtPostalLine(a: PostalLineView | null): string {
  if (!a) return '';
  const street = [a.line1, a.line2].filter(Boolean).join(' ');
  const zip = a.zip4 ? `${a.zip}-${a.zip4}` : a.zip;
  const place = [a.city, [a.state, zip].filter(Boolean).join(' ')].filter(Boolean).join(' ');
  return [street, place].filter(Boolean).join(', ');
}

const norm = (s: string) => s.toUpperCase().replace(/[^A-Z0-9]/g, '');

/**
 * What the Post Office says, in order: its own address (or that it agrees, or found none), then the
 * finding and marks in sentences. `rowReason` is the row's own words ("USPS: Will not arrive"), used
 * only when the database gave no finding.
 */
export function postOfficeLines(f: PostalFactsView | null, rowReason: string | null): { address: string; sentences: string[] } {
  const sentences: string[] = [];
  let address = '';
  if (f) {
    if (f.postOffice) {
      address = f.plan && norm(f.plan.line1) === norm(f.postOffice.line1) ? 'Post Office agrees' : fmtPostalLine(f.postOffice);
    } else if (f.suggested) {
      address = f.suggested;
    } else if (f.result && /no usps match/i.test(f.result)) {
      address = 'Post Office found no such address';
    }
    if (f.result) sentences.push(findingSentence(f.result));
    for (const flag of f.flags) {
      const s = findingSentence(flag);
      if (!sentences.includes(s)) sentences.push(s);
    }
    if (f.dpv) sentences.push(DPV_WORDS[f.dpv.toUpperCase()] ?? UNKNOWN_POST_OFFICE_CODE);
    if (f.vacant && /^y/i.test(f.vacant)) sentences.push('The address is vacant.');
  }
  if (!sentences.length && rowReason) sentences.push(reasonSentence(rowReason));
  return { address, sentences };
}

/** A carrier line in plain words. */
export function carrierPlain(text: string): string {
  const t = text.toLowerCase();
  if (/invalid|not a valid|unallocated|landline|not mobile/.test(t)) return 'The carrier rejected this text: the number is not valid.';
  if (/opt(ed)?[\s-]?out|blocked|stop/.test(t)) return 'The carrier rejected this text: the member has stopped texts from us.';
  return 'The carrier rejected this text.';
}

/** Who said a dialog line, in words. */
export function dialogWho(direction: string): string {
  return direction === 'out' ? 'We sent' : direction === 'carrier' ? 'Carrier' : 'They replied';
}

/** The How column for a row loaded from an earlier list rather than said by the member. */
export function howPlain(r: { seeded: boolean; kind: string; issue: string | null }, methodLabel: string): string {
  if (!r.seeded) return methodLabel;
  const is = (r.issue ?? '').toLowerCase();
  if (r.kind === 'delivery') return 'Address check';
  if (r.kind === 'technical' && is === 'duplicate') return 'Phone on several members';
  if (r.kind === 'technical') return 'Phone check';
  return 'From an earlier list';
}

/** The Source column in words. */
export function sourcePlain(
  r: { basisSource?: string | null; kind: string; seeded: boolean; method: string; evidence?: { file: string } | null },
  postal: PostalFactsView | null,
): string {
  if (r.basisSource) return r.basisSource;
  if (r.kind === 'delivery') {
    // The file the row was loaded from says WHICH check found it; only its kind is said, never its name.
    const file = r.evidence?.file ?? '';
    if (/kit_tracking/i.test(file)) return 'The kit tracking from the Post Office';
    if (/letter/i.test(file)) return 'The letter tracking from the Post Office';
    if (/prelaunch/i.test(file) || (postal?.dpv !== null && postal?.dpv !== undefined)) return 'The address check before the mailing';
    return "The plan's eligibility file, checked by the Post Office";
  }
  if (r.kind === 'technical') return r.seeded ? "The plan's eligibility file" : 'The phone check';
  if (r.method === 'sms') return "The member's text";
  if (r.method === 'voice') return "The member's call";
  return r.seeded ? 'An earlier list' : 'Recorded here';
}

/** "No member has this number" only when NOTHING ties the number to a member (ClariConnect 1:54 AM). */
export function noMemberHasNumber(r: { memberExternalId: string | null; membershipMemberId?: string | null; membershipMembers?: unknown[] }): boolean {
  return !r.memberExternalId && !r.membershipMemberId && !(r.membershipMembers && r.membershipMembers.length);
}

/** A stored line in key=value or code form ("usps_dpv=N", "DPV = N", an E.164 number): never shown outside Technical detail. */
export const CODE_LIKE = /\b[a-z]+_[a-z_]+\s*=|\bDPV\b|=\s*[A-Z]\b|\+\d{10,}/;

/**
 * The check's own line on a loaded row ("Bad phone number", "USPS: Will not arrive"), in plain words,
 * or null when it is in code form and belongs under Technical detail.
 */
export function leadPlain(lead: string): string | null {
  if (CODE_LIKE.test(lead)) return null;
  if (/^usps:/i.test(lead.trim())) return findingSentence(lead);
  return lead.replace(/\s*\((?:the )?roster's own [^)]*\)/i, '').trim() || null;
}
